import {Text} from 'platform/foundation';
import styled from 'styled-components';

import type {MouseEventHandler} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {withAlphaHex} from '../withAlphaHex';

interface AiComposerPillProps extends RequiredTestIdProps {
  title: string;
  isDisabled?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}

// Hex alpha suffixes: 0.22 of the accent for the border, 0.75 of white for the fill.
const ALPHA_BORDER = '38';
const ALPHA_FILL = 'BF';
const HAIRLINE = '1px solid';

export function AiComposerPill(props: AiComposerPillProps) {
  return (
    <Pill
      type="button"
      disabled={props.isDisabled}
      onClick={props.onClick}
      data-testid={suffixTestId('button', props)}
    >
      <Text size="xSmall" color="primary" overflowWrap="anywhere">
        {props.title}
      </Text>
    </Pill>
  );
}

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: ${({theme}) => theme.getSize(6)};
  padding: 0 ${({theme}) => theme.getSize(2)};
  border: ${HAIRLINE}
    ${({theme}) => withAlphaHex(theme.colors.palettes.purple[60][100], ALPHA_BORDER)};
  border-radius: ${({theme}) => theme.radii.circular};
  background: ${({theme}) => withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_FILL)};
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  user-select: none;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:hover:enabled,
  &:focus-visible {
    background: ${({theme}) => theme.colors.palettes.white[10][100]};
    border-color: ${({theme}) => theme.colors.palettes.purple[60][100]};
  }

  &:focus-visible {
    outline: ${({theme}) => theme.outline.default}
      ${({theme}) => theme.colors.palettes.purple[60][100]};
    outline-offset: ${({theme}) => theme.outline.offset};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
